import { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ComputedFields } from "@/services/api/types";
import {
  useUpdateDealMutation,
  useResetFilteredDeals,
  useResetDealsMe,
  useDeals,
  useUpdateDealStageMutation,
  useUpdateDealStatusMutation,
  useDeleteDealMutation
} from "@/pages/deals/hooks";
import {
  useDeletePersonMutation,
  usePeople,
  useResetPeopleInfinite,
  useUpdatePeopleMutation
} from "@/entities/people/hooks";
import {
  useCompanies,
  useDeleteCompanyMutation,
  useResetCompaniesInfinite,
  useUpdateCompanyMutation
} from "@/pages/companies/hooks";
import { useFieldUpdates } from "@/services/hooks/entities/useFieldUpdates";
import { useDealsNavigation } from "@/services/hooks/entities/deal/useDealsNavigation";
import { DealData } from "@/pages/deals/types";
import { CompanyData } from "@/pages/companies/types";
import { PersonData } from "@/entities/people/types";
import { UserData } from "@/entities/users/types";
import { useUsers } from "@/entities/users/hooks";
import { LeadData } from "@/pages/leads/types";
import { ContractData } from "@/pages/contracts/types";
import {
  useDeleteLeadMutation,
  useLeads,
  useResetLeadsInfinite,
  useUnMarkLeadAsStuckMutation,
  useUpdateLeadMutation,
  useUpdateLeadStatusMutation
} from "@/pages/leads/hooks";
import { KanbanContext } from "@/pages/deals/components/KanbanBoard/KanbanProvider";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/entityTypes";
import { useContract } from "@/pages/contracts/hooks";
import {
  usePartners,
  useUpdatePartnerMutation,
  useDeletePartnerMutation,
  useResetPartnersInfinite
} from "@/pages/partners/hooks";
import { PartnerData } from "@/pages/partners/types";
import {
  usePartnerContacts,
  useUpdatePartnerContactMutation,
  useDeletePartnerContactMutation,
  useResetPartnerContactsInfinite
} from "@/pages/partners/contacts/hooks";
import { PartnerContactData } from "@/pages/partners/contacts/types";

// Re-exported so the many existing importers of EntityType keep working; the
// definitions live in a dependency-free module so task-link code can import
// them without cycling back through this file's hooks.
// eslint-disable-next-line react-refresh/only-export-components -- re-exported enum and lookup tables, not components
export * from "@/services/hooks/entities/entityTypes";

type DrawerItem =
  | DealData
  | CompanyData
  | PersonData
  | UserData
  | LeadData
  | ContractData
  | PartnerData
  | PartnerContactData
  | null
  | undefined;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- setters have heterogeneous entity types
type EntitySetterMap = Partial<{
  [key in EntityType]: (item: any) => void;
}>;

export const getEntityHook = (entityType: EntityType | undefined) => {
  if (!entityType) return null;
  switch (entityType) {
    case EntityType.USER:
      // For users, return a wrapped version that converts users to entities
      return (ids: string[]) => {
        const result = useUsers(ids);
        return {
          entities: result.users,
          isSuccess: result.isSuccess,
          error: result.error
        };
      };
    case EntityType.DEAL:
      return useDeals;
    case EntityType.PERSON:
      return usePeople;
    case EntityType.COMPANY:
      return useCompanies;
    case EntityType.LEAD:
      return useLeads;
    case EntityType.PARTNER:
      return usePartners;
    case EntityType.PARTNER_CONTACT:
      return usePartnerContacts;
    case EntityType.CONTRACT:
      // useContract is single-id; wrap to the array-in/entities-out shape
      return (
        ids: string[],
        options: { include_additionals?: boolean } = {}
      ) => {
        const result = useContract(ids[0], {
          include_additionals: options.include_additionals
        });
        return {
          entities: result.data ? [result.data] : [],
          isSuccess: result.isSuccess,
          error: result.error
        };
      };
    default:
      return null;
  }
};

export const useEntityType = (
  itemType: EntityType,
  item: DrawerItem,
  isKanbanLayout?: boolean,
  onUndoDeal?: () => void,
  onGuardrailsError?: (dealId: string, errorData: any) => void,
  forceRefetch?: boolean,
  includeAdditionals?: boolean
) => {
  const navigate = useNavigate();
  const { navigateToDetailedView } = useDealsNavigation();

  const updateDeal = useUpdateDealMutation();
  const updateDealStage = useUpdateDealStageMutation();
  const updateDealStatus = useUpdateDealStatusMutation();
  const updatePerson = useUpdatePeopleMutation();
  const updateCompany = useUpdateCompanyMutation();
  const updateLead = useUpdateLeadMutation();
  const updateLeadStatus = useUpdateLeadStatusMutation();
  const unMarkLeadAsStuck = useUnMarkLeadAsStuckMutation();
  const updatePartner = useUpdatePartnerMutation();
  const updatePartnerContact = useUpdatePartnerContactMutation();
  const deleteDeal = useDeleteDealMutation();
  const deleteLead = useDeleteLeadMutation();
  const deletePerson = useDeletePersonMutation();
  const deleteCompany = useDeleteCompanyMutation();
  const deletePartner = useDeletePartnerMutation();
  const deletePartnerContact = useDeletePartnerContactMutation();
  const resetFilteredDeals = useResetFilteredDeals();
  const resetDealsMe = useResetDealsMe();
  const resetPeople = useResetPeopleInfinite();
  const resetCompanies = useResetCompaniesInfinite();
  const resetLeads = useResetLeadsInfinite();
  const resetPartners = useResetPartnersInfinite();
  const resetPartnerContacts = useResetPartnerContactsInfinite();

  const [deal, setDeal] = useState<DealData | undefined>(undefined);
  const [person, setPerson] = useState<PersonData | undefined>(undefined);
  const [company, setCompany] = useState<CompanyData | undefined>(undefined);
  const [_user, setUser] = useState<UserData | undefined>(undefined);
  const [lead, setLead] = useState<LeadData | undefined>(undefined);
  const [partner, setPartner] = useState<PartnerData | undefined>(undefined);
  const [partnerContact, setPartnerContact] = useState<
    PartnerContactData | undefined
  >(undefined);

  // Track the last fetchedEntity we used to detect new fetches
  const lastFetchedEntityRef = useRef<any>(null);

  // Fetch fresh entity data when forceRefetch is true
  const shouldFetch = useMemo(
    () => !!(item?.id && forceRefetch),
    [item?.id, forceRefetch]
  );

  // Only fetch for the specific entity type to avoid unnecessary API calls
  const isDeal = itemType === EntityType.DEAL;
  const isPerson = itemType === EntityType.PERSON;
  const isCompany = itemType === EntityType.COMPANY;
  const isLead = itemType === EntityType.LEAD;
  const isPartner = itemType === EntityType.PARTNER;
  const isPartnerContact = itemType === EntityType.PARTNER_CONTACT;

  const fetchedDeals = useDeals(
    isDeal && shouldFetch && item?.id ? [item.id] : [],
    {
      computed_fields:
        isDeal && shouldFetch ? ComputedFields.ALL : ComputedFields.NONE,
      include_additionals: includeAdditionals
    }
  );
  const fetchedPeople = usePeople(
    isPerson && shouldFetch && item?.id ? [item.id] : [],
    {
      enabled: isPerson && shouldFetch,
      forceRefetch: isPerson && shouldFetch,
      include_additionals: includeAdditionals
    }
  );
  const fetchedCompanies = useCompanies(
    isCompany && shouldFetch && item?.id ? [item.id] : [],
    {
      enabled: isCompany && shouldFetch,
      forceRefetch: isCompany && shouldFetch,
      include_additionals: includeAdditionals
    }
  );
  const fetchedLeads = useLeads(
    isLead && shouldFetch && item?.id ? [item.id] : [],
    {
      enabled: isLead && shouldFetch,
      forceRefetch: isLead && shouldFetch,
      computed_fields:
        isLead && shouldFetch ? ComputedFields.ALL : ComputedFields.NONE,
      include_additionals: includeAdditionals
    }
  );
  const fetchedPartners = usePartners(
    isPartner && shouldFetch && item?.id ? [item.id] : [],
    {
      enabled: isPartner && shouldFetch,
      forceRefetch: isPartner && shouldFetch,
      computed_fields:
        isPartner && shouldFetch ? ComputedFields.ALL : ComputedFields.NONE,
      include_additionals: includeAdditionals
    }
  );

  const fetchedPartnerContacts = usePartnerContacts(
    isPartnerContact && shouldFetch && item?.id ? [item.id] : [],
    {
      enabled: isPartnerContact && shouldFetch,
      forceRefetch: isPartnerContact && shouldFetch,
      computed_fields:
        isPartnerContact && shouldFetch
          ? ComputedFields.ALL
          : ComputedFields.NONE,
      include_additionals: includeAdditionals
    }
  );

  const fetchedEntity = useMemo(() => {
    switch (itemType) {
      case EntityType.DEAL:
        return fetchedDeals.entities[0];
      case EntityType.PERSON:
        return fetchedPeople.entities[0];
      case EntityType.COMPANY:
        return fetchedCompanies.entities[0];
      case EntityType.LEAD:
        return fetchedLeads.entities[0];
      case EntityType.PARTNER:
        return fetchedPartners.entities[0];
      case EntityType.PARTNER_CONTACT:
        return fetchedPartnerContacts.entities[0];
      default:
        return undefined;
    }
  }, [
    itemType,
    fetchedDeals.entities,
    fetchedPeople.entities,
    fetchedCompanies.entities,
    fetchedLeads.entities,
    fetchedPartners.entities,
    fetchedPartnerContacts.entities
  ]);

  const kanbanContext = useContext(KanbanContext);

  const entitySetters: EntitySetterMap = {
    [EntityType.DEAL]: setDeal,
    [EntityType.PERSON]: setPerson,
    [EntityType.COMPANY]: setCompany,
    [EntityType.USER]: setUser,
    [EntityType.LEAD]: setLead,
    [EntityType.PARTNER]: setPartner,
    [EntityType.PARTNER_CONTACT]: setPartnerContact
  };

  useEffect(() => {
    const setter = entitySetters[itemType];
    if (!setter) return;
    const newEntity = fetchedEntity || item || undefined;

    // Track new fetches to allow QuickView refreshes
    const isNewFetch =
      fetchedEntity && fetchedEntity !== lastFetchedEntityRef.current;
    if (isNewFetch) {
      lastFetchedEntityRef.current = fetchedEntity;
    }
    // Decided when the read lands rather than whenever React runs the updater
    const pendingWrites = pendingWritesByType[itemType];
    const recordHasPendingWrites = !!pendingWrites?.hasPendingWrites(
      newEntity?.id
    );
    // Back on a record whose saves are still going out: its cached copy predates them
    const newEntityWithPendingWrites =
      recordHasPendingWrites && pendingWrites
        ? pendingWrites.withPendingWrites(newEntity)
        : newEntity;

    setter((currentEntity: any) => {
      // Update on initial mount or navigation (different entity ID)
      if (!currentEntity || currentEntity.id !== newEntity?.id) {
        return newEntityWithPendingWrites;
      }
      // Update when forceRefetch is active and we have new fetched data (QuickView refresh)
      if (forceRefetch && isNewFetch) {
        // A read answered before a still-pending save would put back the value it replaces
        if (recordHasPendingWrites) {
          return currentEntity;
        }
        return newEntity;
      }
      // Otherwise keep current entity - mutations update via onSuccess
      return currentEntity;
    });
  }, [fetchedEntity, item, itemType, forceRefetch]);

  useEffect(() => {
    if (isKanbanLayout && kanbanContext?.setDeals && deal) {
      kanbanContext.setDeals((prev) =>
        prev.map((d) => (d.id === deal.id ? deal : d))
      );
    }
  }, [deal, isKanbanLayout]);

  const resetDeals = () => {
    if (
      isKanbanLayout &&
      deal?.stage &&
      kanbanContext?.columnRefetchFunctions[deal.stage]
    ) {
      kanbanContext.columnRefetchFunctions[deal.stage]();
    } else {
      resetFilteredDeals();
      resetDealsMe();
    }
  };

  const { onUpdateField: onUpdateDealField, pendingWrites: dealPendingWrites } =
    useFieldUpdates(
      deal,
      setDeal,
      updateDeal,
      "deals.dealView.updateError",
      resetDeals,
      updateDealStatus,
      updateDealStage,
      undefined,
      onUndoDeal,
      onGuardrailsError
    );

  const {
    onUpdateField: onUpdatePersonField,
    pendingWrites: personPendingWrites
  } = useFieldUpdates(
    person,
    setPerson,
    updatePerson,
    "people.personView.updateError",
    resetPeople
  );

  const {
    onUpdateField: onUpdateCompanyField,
    pendingWrites: companyPendingWrites
  } = useFieldUpdates(
    company,
    setCompany,
    updateCompany,
    "companies.companyView.updateError",
    resetCompanies
  );

  const { onUpdateField: onUpdateLeadField, pendingWrites: leadPendingWrites } =
    useFieldUpdates(
      lead,
      setLead,
      updateLead,
      "leads.leadView.updateError",
      resetLeads,
      undefined,
      undefined,
      updateLeadStatus
    );

  const {
    onUpdateField: onUpdatePartnerField,
    pendingWrites: partnerPendingWrites
  } = useFieldUpdates(
    partner,
    setPartner,
    updatePartner,
    "partners.partnerView.updateError",
    resetPartners
  );

  const {
    onUpdateField: onUpdatePartnerContactField,
    pendingWrites: partnerContactPendingWrites
  } = useFieldUpdates(
    partnerContact,
    setPartnerContact,
    updatePartnerContact,
    "partners.partnerContactView.updateError",
    resetPartnerContacts
  );

  const pendingWritesByType: Partial<
    Record<EntityType, typeof dealPendingWrites>
  > = {
    [EntityType.DEAL]: dealPendingWrites,
    [EntityType.PERSON]: personPendingWrites,
    [EntityType.COMPANY]: companyPendingWrites,
    [EntityType.LEAD]: leadPendingWrites,
    [EntityType.PARTNER]: partnerPendingWrites,
    [EntityType.PARTNER_CONTACT]: partnerContactPendingWrites
  };

  const openDetailedView = (extraParams?: Record<string, string>) => {
    if (itemType === EntityType.DEAL && item?.id) {
      return navigateToDetailedView(item.id, extraParams);
    }
    navigate(`/${entityTypeToEntityName[itemType]}/${item?.id}`);
  };

  return {
    deal,
    person,
    company,
    _user,
    lead,
    partner,
    partnerContact,
    setDeal,
    setLead,
    setPerson,
    setCompany,
    setPartner,
    setPartnerContact,
    onUpdateDealField,
    onUpdatePersonField,
    onUpdateCompanyField,
    onUpdateLeadField,
    onUpdatePartnerField,
    onUpdatePartnerContactField,
    updateLeadStatus,
    unMarkLeadAsStuck,
    openDetailedView,
    deleteDeal,
    deleteLead,
    deletePerson,
    deleteCompany,
    deletePartner,
    deletePartnerContact,
    resetDeals,
    resetCompanies,
    resetLeads,
    resetPeople,
    resetPartners,
    resetPartnerContacts
  };
};
